import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router';
import { z } from 'zod';
import { Button, Logo, TextField } from '@/design-system';
import { ns, useT, zodMessage } from '@/i18n';
import { useNewsletterSignup } from '@/services/queries';
import { paths, resultsUrl } from '../paths';
import { useVehicleSearch } from '../state/vehicleSearch';
import styles from './Footer.module.css';

/** Site footer (`.foot`), with the newsletter and the content pages (P11). */
export function Footer() {
  const t = useT(ns.shell);
  const tc = useT(ns.common);
  const { search } = useVehicleSearch();
  return (
    <footer className={`l-wrap ${styles.foot}`}>
      <div className={styles.grid}>
        <div className={styles.brand}>
          <Logo size={36} wordSize={22} />
          <p className="t-small t-muted">{t('footer.tagline')}</p>
          <Newsletter />
        </div>
        <div>
          <h4>{t('footer.rent')}</h4>
          <Link to={resultsUrl({ ...search, kind: 'car' })}>
            {tc('cars')}
          </Link>
          <Link to={resultsUrl({ ...search, kind: 'moto' })}>
            {tc('motos')}
          </Link>
          <Link to={paths.experiences}>
            {tc('experiences')}
          </Link>
        </div>
        <div>
          <h4>{t('footer.account')}</h4>
          <Link to={paths.trips}>
            {t('menu.bookings')}
          </Link>
          <Link to={paths.favourites}>
            {tc('favourites')}
          </Link>
          <Link to={paths.messages}>
            {tc('messages')}
          </Link>
        </div>
        <div>
          <h4>Cardnd</h4>
          <Link to={paths.partner}>
            {t('becomePartner')}
          </Link>
          <Link to={paths.about}>
            {t('footer.about')}
          </Link>
          <Link to={paths.faq}>
            {t('footer.faq')}
          </Link>
          <Link to={paths.blog}>
            {t('footer.blog')}
          </Link>
          <Link to={paths.contact}>
            {t('footer.contact')}
          </Link>
        </div>
      </div>
      <div className={styles.bottom}>
        <span className={styles.legal}>
          <span>{t.rich('footer.copyright', { year: new Date().getFullYear() })}</span>
          <Link to={paths.terms}>
            {t('footer.terms')}
          </Link>
          <Link to={paths.privacy}>
            {t('footer.privacy')}
          </Link>
        </span>
        <span>{t('footer.prices')}</span>
      </div>
    </footer>
  );
}

const schema = z.object({
  email: z
    .string()
    .trim()
    .min(1, zodMessage(ns.shell, 'newsletter.required'))
    .email(zodMessage(ns.shell, 'newsletter.invalid')),
});
type Values = z.infer<typeof schema>;

/** Newsletter (API-CNT-3): an address already subscribed counts as subscribed. */
function Newsletter() {
  const t = useT(ns.shell);
  const signup = useNewsletterSignup();
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<Values>({ resolver: zodResolver(schema), defaultValues: { email: '' } });
  const submit = handleSubmit((v) => signup.mutate(v.email, { onSuccess: () => reset() }));

  return (
    <form className={styles.news} onSubmit={(e) => void submit(e)} noValidate aria-label={t('newsletter.label')}>
      <b className={styles.newsTitle}>{t('newsletter.title')}</b>
      <div className={styles.newsRow}>
        <TextField
          label={t('newsletter.field')}
          type="email"
          inputMode="email"
          autoComplete="email"
          placeholder={t('newsletter.placeholder')}
          error={errors.email?.message}
          className={styles.newsField}
          {...register('email', { onChange: () => signup.reset() })}
        />
        <Button type="submit" size="sm" loading={signup.isPending} className={styles.newsBtn}>
          {t('newsletter.submit')}
        </Button>
      </div>
      <p className={styles.newsNote} role="status" aria-live="polite">
        {signup.isSuccess ? t('newsletter.done') : signup.isError ? signup.error.message : ''}
      </p>
    </form>
  );
}
